import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import {
  OrganizationEntry,
  PersonEntry,
  PersonWrapperEntry,
} from "frontend/contentful/schema/semantics";
import { createPersonImageProps } from "../primitives/media-asset.props";
import { useContentful } from "frontend/hooks/use-contentful";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { PersonTeaseProps } from "design-system/components/primitives/person-tease/person-tease";
import { MediaAssetProps } from "design-system/components/primitives/media-asset/media-asset";
import { createSocialMediaLinksProps } from "../universal/social-media-links.props";
import { RenderContentfulRichText } from "./render-contentful-rich-text";

export interface PersonAllProps extends PersonTeaseProps {
  personName?: string;
  link?: string;
  avatar?: MediaAssetProps;
  nationality?: string;
  lifespan?: string;
}

// Universal function to be used sitewide and will returns ALL the props needed for a person
// PersonAllProps includes PersonTeaseProps & BylineAuthorProps, add more props as needed
export const getPersonProps = createContentfulProps<
  PersonEntry | PersonWrapperEntry | OrganizationEntry,
  PersonAllProps
>(({ entry, createEditAttributes }) => {
  const { getPersonLink, getEntityName, getEntry, getEntries } =
    useContentful();

  let props: PersonAllProps = { name: "", titles: [], type: "Person" };

  let personEntry: PersonEntry | undefined = undefined;
  if (!entry) return props;

  if (isContentType<PersonWrapperEntry>("personWrapper", entry)) {
    const overridePerson = entry.fields.person
      ? getEntry(entry.fields.person)
      : undefined;
    if (overridePerson) personEntry = overridePerson;
  } else if (isContentType<PersonEntry>("person", entry)) {
    personEntry = entry;
  }

  const name = getEntityName(entry);
  const mediaAsset = createPersonImageProps(entry);
  const link = getPersonLink(entry);

  if (personEntry) {
    props = {
      type: "Person",
      name: name,
      personName: name, // allow for string version when needed
      bio: personEntry.fields.bio ? (
        <RenderContentfulRichText document={personEntry.fields.bio} />
      ) : null,
      social: createSocialMediaLinksProps(personEntry),
      link,
      email: personEntry.fields.email,
      phone: personEntry.fields.phone,
      titles: Array.isArray(personEntry.fields.jobTitles)
        ? personEntry.fields.jobTitles
        : [],
      portrait: mediaAsset?.image,
      avatar: mediaAsset,

      editAttributes: {
        name: createEditAttributes({
          entry: personEntry,
          fieldId: "firstName",
        }),
        titles: createEditAttributes({
          entry: personEntry,
          fieldId: "jobTitles",
        }),
      },
    };

    let nationality: string | undefined = undefined;

    (getEntries(personEntry.fields?.nationality) ?? []).forEach((select) => {
      nationality = nationality
        ? `${nationality}, ${select.fields.title}`
        : select.fields.title;
    });
    if (nationality) {
      props.nationality = nationality;
    }

    let lifespan: string | undefined = undefined;
    if (personEntry.fields.birthYear && personEntry.fields.deathYear) {
      lifespan = `${personEntry.fields.birthYear}-${personEntry.fields.deathYear}`;
    } else if (personEntry.fields.birthYear && !personEntry.fields.deathYear) {
      lifespan = `born ${personEntry.fields.birthYear}`;
    }
    if (lifespan) {
      props.lifespan = lifespan;
    }

    if (props) {
      if (isContentType<PersonEntry>("person", entry)) {
        /* Short term fix for treatment of department in Person Listing: CMS-2185  */
        if (personEntry && entry.fields.organization) {
          const org = getEntry(personEntry.fields.organization);

          if (org?.fields?.title) {
            props.organization = org.fields.title;
          }
        }
      }

      if (isContentType<PersonWrapperEntry>("personWrapper", entry)) {
        //overrides
        if (entry.fields.bio) {
          props.bio = entry.fields.bio ? (
            <RenderContentfulRichText document={entry.fields.bio} />
          ) : null;
        }
        if (entry.fields.email) {
          props.email = entry.fields.email;
        }
        if (Array.isArray(entry.fields.jobTitles)) {
          props.titles = entry.fields.jobTitles;
        }
      }
    }
  }

  if (isContentType<OrganizationEntry>("organization", entry)) {
    props.name = entry.fields.title;
    props.personName = entry.fields.title;
  }

  return props;
});
